<template>
    <portal to="cookieConsent">
        <div v-if="showBanner" class="cookie-consent-banner">
            <div class="cookie-banner-content">
                <div class="cookie-banner-header">
                    <div class="cookie-text">
                        <h3>Datenschutzeinstellungen</h3>
                        <p>
                            Wir respektieren Ihre Privatsphäre und verwenden Cookies nur mit Ihrer
                            Einwilligung.
                        </p>
                    </div>
                </div>

                <div class="cookie-categories">
                    <div class="cookie-category essential">
                        <div class="category-header">
                            <i class="ri-shield-check-line"></i>
                            <div class="category-info">
                                <h4>Notwendige Cookies</h4>
                                <span class="category-required">Immer aktiv</span>
                            </div>
                        </div>
                        <p>Technisch erforderlich für die Grundfunktionen der Website.</p>
                    </div>

                    <div class="cookie-category marketing">
                        <div class="category-header">
                            <i class="ri-bar-chart-line"></i>
                            <div class="category-info">
                                <h4>Marketing-Cookies</h4>
                                <label class="toggle-switch">
                                    <input
                                        type="checkbox"
                                        v-model="preferences.marketing"
                                        @change="updatePreferences"
                                    />
                                    <span class="slider"></span>
                                </label>
                            </div>
                        </div>
                        <p>Helfen uns, Ihnen relevante Werbung zu zeigen.</p>
                    </div>
                </div>

                <div class="cookie-actions">
                    <SiteButton @onClick="acceptEssentialOnly" variant="outline">
                        Nur notwendige akzeptieren
                    </SiteButton>
                    <SiteButton @onClick="acceptAll" variant="success">
                        Alle Cookies akzeptieren
                    </SiteButton>
                </div>

                <div class="cookie-footer">
                    <p class="cookie-links">
                        <a href="/privacy-policy" target="_blank">Datenschutz</a>
                        <span>•</span>
                        <a href="/impressum" target="_blank">Impressum</a>
                    </p>
                </div>
            </div>
        </div>

        <div v-if="showModal" class="cookie-modal-overlay" @click="closeModal">
            <div class="cookie-modal" @click.stop>
                <div class="modal-header">
                    <h3>Cookie-Einstellungen im Detail</h3>
                    <button @click="closeModal" class="modal-close">
                        <i class="ri-close-line"></i>
                    </button>
                </div>

                <div class="modal-content">
                    <div class="cookie-details">
                        <div class="detail-section">
                            <h4>Notwendige Cookies</h4>
                            <p>
                                Diese Cookies sind für die Grundfunktionalität unserer Website
                                unerlässlich und können nicht deaktiviert werden. Sie werden nur für
                                technische Zwecke verwendet:
                            </p>
                            <ul>
                                <li>
                                    <strong>Session-Management:</strong> Ermöglicht die Navigation
                                    auf der Website
                                </li>
                                <li>
                                    <strong>Sicherheit:</strong> Schützt vor Betrug und Missbrauch
                                </li>
                                <li>
                                    <strong>Performance:</strong> Stellt sicher, dass die Website
                                    ordnungsgemäß funktioniert
                                </li>
                            </ul>
                        </div>

                        <div class="detail-section">
                            <h4>Marketing-Cookies</h4>
                            <p>
                                Diese Cookies helfen uns zu verstehen, wie Besucher mit unserer
                                Website interagieren. Die Daten werden verwendet, um:
                            </p>
                            <ul>
                                <li>
                                    <strong>Personalisierte Werbung:</strong> Relevante Anzeigen auf
                                    anderen Websites
                                </li>
                                <li>
                                    <strong>Conversion-Tracking:</strong> Messung der Effektivität
                                    unserer Marketingkampagnen
                                </li>
                                <li>
                                    <strong>Website-Analysen:</strong> Verbesserung der
                                    Nutzererfahrung
                                </li>
                            </ul>
                            <div class="cookie-provider">
                                <p><strong>Verwendete Dienste:</strong> Meta Pixel (Facebook)</p>
                                <p>
                                    <strong>Rechtsgrundlage:</strong> Einwilligung gemäß Art. 6 Abs.
                                    1 lit. a DSGVO
                                </p>
                            </div>
                        </div>

                        <div class="gdpr-notice">
                            <h4>Ihre Rechte</h4>
                            <p>Sie haben das Recht:</p>
                            <ul>
                                <li>Ihre Einwilligung jederzeit zu widerrufen</li>
                                <li>Auskunft über Ihre gespeicherten Daten zu erhalten</li>
                                <li>Die Löschung Ihrer Daten zu verlangen</li>
                                <li>
                                    Eine Beschwerde bei der zuständigen Aufsichtsbehörde einzulegen
                                </li>
                            </ul>
                            <p>
                                Kontakt:
                                <a href="mailto:datenschutz@taxifusion.de"
                                    >datenschutz@taxifusion.de</a
                                >
                            </p>
                        </div>
                    </div>

                    <div class="modal-actions">
                        <SiteButton @onClick="saveAndClose" variant="success">
                            Einstellungen speichern
                        </SiteButton>
                    </div>
                </div>
            </div>
        </div>
    </portal>
</template>

<script>
import SiteButton from '@/components/SiteButton';

export default {
    name: 'CookieConsent',
    components: {
        SiteButton,
    },
    data() {
        return {
            showBanner: false,
            showModal: false,
            preferences: {
                essential: true,
                marketing: false,
            },
        };
    },
    mounted() {
        this.checkConsentStatus();
    },
    methods: {
        checkConsentStatus() {
            const saved = localStorage.getItem('cookie-consent');
            if (!saved) {
                this.showBanner = true;
                this.preferences = { essential: true, marketing: false };
            } else {
                try {
                    const consent = JSON.parse(saved);
                    this.showBanner = false;
                    this.preferences = consent.preferences || { essential: true, marketing: false };
                } catch (error) {
                    console.warn('Failed to parse cookie consent:', error);
                    this.showBanner = true;
                    this.preferences = { essential: true, marketing: false };
                }
            }
        },

        updatePreferences() {
            this.savePreferences();
        },

        acceptAll() {
            this.preferences.marketing = true;
            this.saveAndClose();
        },

        acceptEssentialOnly() {
            this.preferences.marketing = false;
            this.saveAndClose();
        },

        showDetails() {
            this.showModal = true;
        },

        closeModal() {
            this.showModal = false;
        },

        saveAndClose() {
            this.savePreferences();
            this.showBanner = false;
            this.showModal = false;
        },

        savePreferences() {
            const consent = {
                preferences: this.preferences,
                timestamp: new Date().toISOString(),
                version: '1.0',
                consentId: this.generateConsentId(),
            };

            localStorage.setItem('cookie-consent', JSON.stringify(consent));

            window.dispatchEvent(
                new CustomEvent('cookie-consent-updated', {
                    detail: this.preferences,
                }),
            );
        },

        generateConsentId() {
            return 'consent_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        },

        showCookieSettings() {
            this.showBanner = false;
        },
    },
};
</script>

<style lang="scss">
.cookie-consent-banner {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-top: 0.5px solid rgba(255, 255, 255, 0.2);
    box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.12);
    z-index: 10001;
    animation: slideUp 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.cookie-banner-content {
    max-width: 1200px;
    margin: 0 auto;
    padding: 1rem 1.25rem;

    @media (max-width: $breakpoint-mobile) {
        padding: 0.75rem 0.75rem;
    }
}

.cookie-banner-header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.75rem;

    .cookie-icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 48px;
        height: 48px;
        background: linear-gradient(135deg, rgba(95, 235, 186, 0.1), rgba(9, 212, 144, 0.1));
        border: 0.5px solid rgba(95, 235, 186, 0.2);
        border-radius: 12px;
        color: #09d490;
        font-size: 1.5rem;
    }

    .cookie-text {
        flex: 1;

        h3 {
            margin: 0 0 0.25rem 0;
            font-size: 1rem;
            font-weight: 600;
            color: var(--color-blue-dark);
            letter-spacing: -0.01em;
        }

        p {
            margin: 0;
            font-size: 0.8rem;
            color: rgba(0, 0, 0, 0.6);
            line-height: 1.4;
        }
    }

    .btn-settings {
        padding: 0;
        width: 40px;
        height: 40px;

        i {
            font-size: 1.2rem;
        }
    }
}

.cookie-categories {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    margin-bottom: 0.75rem;

    @media (max-width: $breakpoint-tablet-portrait) {
        grid-template-columns: 1fr;
    }
}

.cookie-category {
    background: rgba(255, 255, 255, 0.8);
    border: 0.5px solid rgba(0, 0, 0, 0.08);
    border-radius: 12px;
    padding: 0.75rem;
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    &:hover {
        background: rgba(255, 255, 255, 0.9);
        border-color: rgba(0, 0, 0, 0.12);
    }

    &.essential {
        background: rgba(76, 175, 80, 0.05);
        border-color: rgba(76, 175, 80, 0.2);
    }

    &.marketing {
        background: rgba(255, 152, 0, 0.05);
        border-color: rgba(255, 152, 0, 0.2);
    }

    .category-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.25rem;

        i {
            font-size: 1.1rem;
            color: var(--color-blue-dark);
        }

        .category-info {
            flex: 1;

            h4 {
                margin: 0 0 0.25rem 0;
                font-size: 0.95rem;
                font-weight: 600;
                color: var(--color-blue-dark);
            }
        }
    }

    p {
        margin: 0;
        font-size: 0.8rem;
        color: rgba(0, 0, 0, 0.7);
        line-height: 1.4;
    }
}

.category-required {
    background: rgba(76, 175, 80, 0.1);
    color: #4caf50;
    padding: 0.15rem 0.4rem;
    border-radius: 8px;
    font-size: 0.65rem;
    font-weight: 600;
    letter-spacing: 0.02em;
}

.toggle-switch {
    position: relative;
    display: inline-block;
    width: 40px;
    height: 22px;

    input {
        opacity: 0;
        width: 0;
        height: 0;

        &:checked + .slider {
            background-color: #ff9800;

            &:before {
                transform: translateX(18px);
            }
        }
    }

    .slider {
        position: absolute;
        cursor: pointer;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background-color: #ccc;
        transition: 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
        border-radius: 22px;

        &:before {
            position: absolute;
            content: '';
            height: 16px;
            width: 16px;
            left: 3px;
            bottom: 3px;
            background-color: white;
            transition: 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
            border-radius: 50%;
            box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }
    }
}

.cookie-actions {
    display: flex;
    gap: 0.5rem;
    justify-content: flex-end;
    align-items: center;
    margin-bottom: 0.5rem;

    @media (max-width: $breakpoint-mobile) {
        flex-direction: column;
        gap: 0.5rem;
        align-items: stretch;
    }
}

.cookie-footer {
    text-align: center;

    .cookie-links {
        margin: 0;
        font-size: 0.75rem;
        color: rgba(0, 0, 0, 0.5);

        a {
            color: var(--color-blue-dark);
            text-decoration: none;
            transition: color 0.3s ease;

            &:hover {
                color: var(--color-blue-light);
            }
        }

        span {
            margin: 0 0.5rem;
        }
    }
}

.cookie-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(5px);
    -webkit-backdrop-filter: blur(5px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10002;
    padding: 1rem;
    animation: fadeIn 0.3s ease-out;
}

.cookie-modal {
    background: rgba(255, 255, 255, 0.98);
    backdrop-filter: blur(20px);
    -webkit-backdrop-filter: blur(20px);
    border-radius: 16px;
    max-width: 600px;
    width: 100%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
    border: 0.5px solid rgba(255, 255, 255, 0.2);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.5rem;
    border-bottom: 0.5px solid rgba(0, 0, 0, 0.08);

    h3 {
        margin: 0;
        font-size: 1.25rem;
        font-weight: 600;
        color: var(--color-blue-dark);
        letter-spacing: -0.01em;
    }

    .modal-close {
        background: none;
        border: none;
        font-size: 1.5rem;
        cursor: pointer;
        color: rgba(0, 0, 0, 0.5);
        padding: 0.25rem;
        border-radius: 8px;
        transition: all 0.3s ease;

        &:hover {
            background: rgba(0, 0, 0, 0.05);
            color: var(--color-blue-dark);
        }
    }
}

.modal-content {
    padding: 1.5rem;
}

.cookie-details {
    margin-bottom: 2rem;

    .detail-section {
        margin-bottom: 2rem;

        &:last-child {
            margin-bottom: 0;
        }

        h4 {
            margin: 0 0 0.75rem 0;
            font-size: 1.1rem;
            font-weight: 600;
            color: var(--color-blue-dark);
        }

        p {
            color: rgba(0, 0, 0, 0.7);
            margin-bottom: 1rem;
            line-height: 1.5;
        }

        ul {
            margin: 0 0 1rem 0;
            padding-left: 1.25rem;

            li {
                color: rgba(0, 0, 0, 0.7);
                margin-bottom: 0.5rem;
                line-height: 1.4;

                strong {
                    color: var(--color-blue-dark);
                }
            }
        }
    }

    .cookie-provider {
        background: rgba(255, 152, 0, 0.05);
        border: 0.5px solid rgba(255, 152, 0, 0.1);
        border-radius: 8px;
        padding: 1rem;
        margin-top: 1rem;

        p {
            margin: 0 0 0.5rem 0;
            font-size: 0.9rem;

            &:last-child {
                margin-bottom: 0;
            }

            strong {
                color: var(--color-blue-dark);
            }
        }
    }
}

.gdpr-notice {
    background: rgba(95, 235, 186, 0.05);
    border: 0.5px solid rgba(95, 235, 186, 0.1);
    border-radius: 8px;
    padding: 1rem;

    h4 {
        margin: 0 0 0.75rem 0;
        font-size: 1rem;
        font-weight: 600;
        color: var(--color-blue-dark);
    }

    p {
        margin: 0 0 1rem 0;
        font-size: 0.9rem;
        color: rgba(0, 0, 0, 0.7);
    }

    ul {
        margin: 0 0 1rem 0;
        padding-left: 1.25rem;

        li {
            color: rgba(0, 0, 0, 0.7);
            margin-bottom: 0.25rem;
            font-size: 0.9rem;
        }
    }

    a {
        color: var(--color-blue-light);
        text-decoration: none;

        &:hover {
            text-decoration: underline;
        }
    }
}

.modal-actions {
    text-align: center;
}

@keyframes slideUp {
    from {
        transform: translateY(100%);
        opacity: 0;
    }
    to {
        transform: translateY(0);
        opacity: 1;
    }
}

@keyframes fadeIn {
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
}

/* Override SiteButton outline variant for better contrast in cookie consent */
.cookie-consent-banner .SiteButton.is-outline span {
    color: #b8860b;
}

.cookie-consent-banner .SiteButton.is-outline:hover span {
    color: #8b4513;
}
</style>
